import { useEffect, useMemo } from 'react';
import { useFeatureFlag, useFeatureValue } from 'new/hooks/useFeatureFlags';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import { useFetchCapsules } from 'new/stores/capsules/capsules.api';
import type { CapsuleDto } from 'new/stores/capsules/capsules.endpoints';
import { resolveRuntimeEnabled } from 'new/constants/featureFlags';
import type { RuntimeSelectionConfig } from 'new/constants/featureFlags';

export function useRuntimeClusters(
  flowType: string,
  connectionType: string,
): {
  clusters: CapsuleDto[];
  isEnabled: boolean;
  isLoading: boolean;
  isUninitialized: boolean;
  isError: boolean;
} {
  const capsulesSettingsEnabled = useFeatureFlag('UI_ENABLE_CAPSULES_SETTINGS');
  const runtimeConfig = useFeatureValue<RuntimeSelectionConfig>('UI_ENABLE_RUNTIME_SELECTION');
  const org = useCurrentOrg();
  const { fetchCapsules, data, isUninitialized, isLoading, isError } = useFetchCapsules();

  const isEnabled = resolveRuntimeEnabled(
    capsulesSettingsEnabled,
    runtimeConfig,
    connectionType,
    flowType,
  );

  useEffect(() => {
    if (flowType !== 'streaming' || !isEnabled || !org?.id) {
      return;
    }
    fetchCapsules({ org_id: org.id });
  }, [flowType, isEnabled, org?.id, fetchCapsules]);

  const clusters: CapsuleDto[] = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data)) {
      return data as CapsuleDto[];
    }
    if (!data.byId?.length) return [];
    return data.byId
      .map((id: string) => data.entities[id])
      .filter((c: CapsuleDto | null): c is CapsuleDto => c != null);
  }, [data]);

  return {
    clusters,
    isEnabled,
    isLoading,
    isUninitialized,
    isError,
  };
}
